import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, HandCoins, FileText, Calculator, TrendingUp } from "lucide-react";
import { HeroBuyButton } from "../common/ServiceOrderBundle";
import { AGENCY_PACKAGES } from "../../data/personaContent";

export const PersonaHeader = ({ ctaLabel = "Balíky a ceny" }) => (
  <header className="sticky top-0 z-40 bg-[#0a0a0f]/80 backdrop-blur-xl border-b border-white/10">
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2" data-testid="persona-logo-link">
        <div className="w-8 h-8 bg-[#a855f7] flex items-center justify-center"><TrendingUp className="w-5 h-5 text-white" /></div>
        <span className="text-lg font-black font-['Cabinet_Grotesk'] text-white">TopRankPulse</span>
      </Link>
      <a href="#balicky" className="text-xs sm:text-sm font-bold uppercase tracking-wider text-white/80 hover:text-white transition-colors" data-testid="persona-header-packages-link">
        {ctaLabel}
      </a>
    </div>
  </header>
);

export const PersonaHero = ({ page, isAgency }) => (
  <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-14 sm:pt-20 pb-8 grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-14 items-center">
    <div className="persona-reveal">
      <span data-testid="persona-badge" className="inline-block px-3 py-1 mb-6 text-[11px] font-bold uppercase tracking-[0.2em] text-[#c084fc] border border-[#a855f7]/40 rounded-full font-['JetBrains_Mono']">
        {page.badge}
      </span>
      <h1 data-testid="persona-headline" className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Cabinet_Grotesk'] text-white leading-[1.05] tracking-tight">
        {page.headline} <span className="text-[#c084fc]">{page.highlight}</span>
      </h1>
      <p className="mt-6 text-base md:text-lg text-white/75 leading-relaxed max-w-xl">{page.sub}</p>
      <div className="mt-8 flex flex-wrap items-center gap-4">
        <a href="#balicky" data-testid="persona-hero-cta" className="group inline-flex items-center gap-2 px-7 py-4 bg-[#a855f7] hover:bg-[#9333ea] text-white rounded-full font-black uppercase tracking-wider text-sm transition-colors shadow-lg shadow-[#a855f7]/30">
          {isAgency ? "Pozrieť balíky" : "Chcem byť odporúčaný"}
          <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
        </a>
        {!isAgency && (
          <Link to="/" data-testid="persona-free-audit-link" className="text-sm font-bold text-white/80 underline underline-offset-4 hover:text-white">
            Bezplatný AI audit
          </Link>
        )}
      </div>
    </div>
    <div className="persona-reveal persona-delay-1 relative">
      <div className="absolute -inset-4 bg-[#a855f7]/20 blur-3xl rounded-full pointer-events-none" aria-hidden />
      <img src={page.image} alt={page.headline} data-testid="persona-hero-image" className="relative w-full aspect-square object-cover rounded-2xl border border-white/10" />
    </div>
  </section>
);

export const StepsBlock = ({ steps }) => (
  <section className="max-w-6xl mx-auto px-5 sm:px-8 py-12" data-testid="persona-steps">
    <h2 className="text-base md:text-lg font-bold uppercase tracking-[0.2em] text-white/60 font-['JetBrains_Mono'] mb-8">Ako to funguje</h2>
    <div className="grid md:grid-cols-3 gap-6">
      {steps.map((s, i) => (
        <div key={s.title} className="border-l-2 border-[#a855f7] pl-5">
          <p className="text-4xl font-black text-[#a855f7]/50 font-['JetBrains_Mono'] mb-2">0{i + 1}</p>
          <h3 className="text-lg font-black text-white font-['Cabinet_Grotesk'] mb-2">{s.title}</h3>
          <p className="text-sm text-white/70 leading-relaxed">{s.text}</p>
        </div>
      ))}
    </div>
  </section>
);

const PackageCard = ({ pkg, isAgency }) => (
  <div data-testid={`persona-package-${pkg.id}`} className={`relative rounded-2xl border p-6 sm:p-7 flex flex-col transition-transform duration-300 hover:-translate-y-1 ${pkg.recommended ? "border-[#a855f7] bg-[#a855f7]/10" : "border-white/10 bg-white/[0.03]"}`}>
    {pkg.recommended && (
      <span className="absolute -top-3 left-6 px-3 py-1 text-[10px] font-black uppercase tracking-wider bg-[#a855f7] text-white rounded-full">Najvýhodnejší</span>
    )}
    <h3 className="text-2xl font-black text-white font-['Cabinet_Grotesk']">{pkg.name}</h3>
    {isAgency && <p className="text-sm text-white/60 mt-1">do {pkg.clients} klientov · €{(pkg.price / pkg.clients).toFixed(2).replace(".", ",")} / klient</p>}
    <p className="mt-4 text-4xl font-black text-white font-['JetBrains_Mono']">€{pkg.price}<span className="text-base text-white/50">/mes</span></p>
    <ul className="mt-5 space-y-2.5 flex-1">
      {pkg.bullets.map((b) => (
        <li key={b} className="flex gap-2 text-sm text-white/80"><CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />{b}</li>
      ))}
    </ul>
    <div className="mt-6">
      <HeroBuyButton packageId={pkg.id} priceEur={pkg.price} label={`Objednať ${pkg.name}`} testid={`persona-buy-${pkg.id}`} />
    </div>
  </div>
);

export const PackagesGrid = ({ packages, isAgency }) => (
  <section id="balicky" className="max-w-6xl mx-auto px-5 sm:px-8 py-16 scroll-mt-20" data-testid="persona-packages">
    <h2 className="text-3xl sm:text-4xl font-black text-white font-['Cabinet_Grotesk'] mb-3">
      {isAgency ? "Balíky podľa počtu klientov" : "Vyberte si plán"}
    </h2>
    <p className="text-white/60 mb-10 text-sm sm:text-base">Mesačná platba · zrušiteľné kedykoľvek · bez skrytých poplatkov</p>
    <div className="grid md:grid-cols-3 gap-6">
      {packages.map((p) => <PackageCard key={p.id} pkg={p} isAgency={isAgency} />)}
    </div>
  </section>
);

const fmt = (n) => `€${Math.round(n).toLocaleString("sk-SK")}`;

export const MarginCalculator = () => {
  const [clients, setClients] = useState(10);
  const [resale, setResale] = useState(190);
  const pkg = AGENCY_PACKAGES.find((p) => p.clients >= clients) || AGENCY_PACKAGES[AGENCY_PACKAGES.length - 1];
  const revenue = clients * resale;
  const profit = revenue - pkg.price;
  const margin = revenue > 0 ? Math.round((profit / revenue) * 100) : 0;
  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-8 py-12" data-testid="margin-calculator">
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-10 grid lg:grid-cols-2 gap-10">
        <div>
          <div className="flex items-center gap-2 mb-4"><Calculator className="w-5 h-5 text-[#c084fc]" /><h2 className="text-2xl font-black text-white font-['Cabinet_Grotesk']">Kalkulačka marže</h2></div>
          <label className="block text-sm text-white/70 mb-2">Počet klientov: <strong className="text-white" data-testid="margin-clients-value">{clients}</strong></label>
          <input type="range" min="1" max="50" value={clients} onChange={(e) => setClients(Number(e.target.value))} className="w-full accent-[#a855f7] mb-6" data-testid="margin-clients-slider" />
          <label className="block text-sm text-white/70 mb-2">Vaša cena pre klienta (€/mes)</label>
          <input type="number" min="0" value={resale} onChange={(e) => setResale(Math.max(0, Number(e.target.value)))} className="w-full px-4 py-3 bg-white/5 border border-white/15 rounded-lg text-white focus:outline-none focus:border-[#a855f7]" data-testid="margin-resale-input" />
        </div>
        <div className="grid grid-cols-2 gap-4 content-center">
          <Stat label="Vhodný balík" value={`${pkg.name} · €${pkg.price}`} testid="margin-package" />
          <Stat label="Tržby mesačne" value={fmt(revenue)} testid="margin-revenue" />
          <Stat label="Zisk mesačne" value={fmt(profit)} testid="margin-profit" accent />
          <Stat label="Marža" value={`${margin} %`} testid="margin-pct" accent />
        </div>
      </div>
    </section>
  );
};

const Stat = ({ label, value, testid, accent }) => (
  <div className="rounded-xl bg-black/30 border border-white/10 p-4">
    <p className="text-[11px] uppercase tracking-wider text-white/50 font-['JetBrains_Mono'] mb-1">{label}</p>
    <p data-testid={testid} className={`text-xl sm:text-2xl font-black font-['JetBrains_Mono'] ${accent ? "text-emerald-400" : "text-white"}`}>{value}</p>
  </div>
);

export const AgencyPerksBlock = () => (
  <section className="max-w-6xl mx-auto px-5 sm:px-8 py-12 grid md:grid-cols-2 gap-6" data-testid="agency-perks">
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
      <FileText className="w-7 h-7 text-[#c084fc] mb-4" />
      <h3 className="text-xl font-black text-white font-['Cabinet_Grotesk'] mb-2">Reporty pod vlastnou značkou</h3>
      <p className="text-sm text-white/70 leading-relaxed">Nahráte logo a farby. Klient dostane PDF alebo zdieľaný odkaz s vašou značkou. O nás nevie.</p>
    </div>
    <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6 sm:p-8">
      <HandCoins className="w-7 h-7 text-emerald-400 mb-4" />
      <h3 className="text-xl font-black text-white font-['Cabinet_Grotesk'] mb-2">30 % partnerská provízia doživotne</h3>
      <p className="text-sm text-white/70 leading-relaxed mb-4">Za každého klienta, ktorý si cez váš odkaz kúpi vlastný plán, dostávate 30 % z každej jeho platby, kým platí.</p>
      <Link to="/affiliate" data-testid="agency-partner-signup-link" className="inline-flex items-center gap-2 text-sm font-bold text-emerald-300 hover:text-emerald-200">
        Získať partnerský odkaz <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  </section>
);

export const FaqBlock = ({ faq }) => (
  <section className="max-w-3xl mx-auto px-5 sm:px-8 py-12" data-testid="persona-faq">
    <h2 className="text-2xl sm:text-3xl font-black text-white font-['Cabinet_Grotesk'] mb-6">Časté otázky</h2>
    <div className="divide-y divide-white/10 border-y border-white/10">
      {faq.map((f, i) => (
        <details key={f.q} className="group py-4" data-testid={`persona-faq-${i}`}>
          <summary className="cursor-pointer list-none flex justify-between gap-4 font-bold text-white">
            {f.q}<span className="text-[#c084fc] transition-transform group-open:rotate-45">+</span>
          </summary>
          <p className="mt-3 text-sm text-white/70 leading-relaxed">{f.a}</p>
        </details>
      ))}
    </div>
  </section>
);
